import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authService, medicalRecordService, patientService } from '@/services/api';
import { patientApi } from '@/services/clinicApi';
import { resolveBackendUrl } from '@/config/apiConfig';
import { useAuth } from './AuthContext';

const FamilyContext = createContext(null);
const STORAGE_KEY = 'familyMembers';
const HIDDEN_PATIENT_IDS_KEY = 'hiddenPatientIds';
const PATIENT_IMAGE_VERSIONS_KEY = 'patientImageVersions';
const ALCOHOL_NONE = 'none';
const EXERCISE_NONE = 'none';

const readMap = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }

  return {};
};

const readList = (value) => (Array.isArray(value) ? value : []);

const readString = (value) => {
  if (value == null || value === false) {
    return '';
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    for (const key of ['name', 'display_name', 'en', 'ar', 'value']) {
      const resolved = readString(value[key]);
      if (resolved) {
        return resolved;
      }
    }

    return '';
  }

  return String(value).trim();
};

const readBool = (value, fallback = false) => {
  if (value == null) {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) {
    return true;
  }
  if (['false', '0', 'no', 'n', ''].includes(normalized)) {
    return false;
  }

  return fallback;
};

const readNumberString = (value) => {
  if (value == null || value === '') {
    return '';
  }

  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed === 0) {
    return '';
  }

  return Number.isInteger(parsed) ? String(parsed) : String(parsed);
};

const isFileLike = (value) =>
  (typeof File !== 'undefined' && value instanceof File) ||
  (typeof Blob !== 'undefined' && value instanceof Blob);

const normalizeImageUrl = (value) => {
  const image = readString(value);
  if (!image) {
    return '';
  }

  if (
    image.startsWith('http://') ||
    image.startsWith('https://') ||
    image.startsWith('data:')
  ) {
    return image;
  }

  if (image.startsWith('/')) {
    return resolveBackendUrl(image);
  }

  return image;
};

const resolvePatientImageUrl = (item) =>
  normalizeImageUrl(
    item.image ||
      item.image_url ||
      item.avatar ||
      item.profile_image ||
      item.photo ||
      item.media?.[0]?.url ||
      item.media?.[0]?.thumb
  );

const resolvePatientImageVersion = (item) =>
  readString(
    item.image_updated_at ||
      item.profile_image_updated_at ||
      item.avatar_updated_at ||
      item.photo_updated_at ||
      item.updated_at ||
      item.write_date ||
      item.media?.[0]?.updated_at ||
      item.media?.[0]?.write_date ||
      item.media?.[0]?.id
  );

const splitName = (name) => {
  const parts = readString(name).split(/\s+/).filter(Boolean);

  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

const extractNames = (items) =>
  readList(items)
    .map((item) => readString(readMap(item).name || item))
    .filter(Boolean);

const toListPayload = (items) =>
  extractNames(items).map((name) => ({ name }));

const getHiddenPatientIdsKey = (user) =>
  `${HIDDEN_PATIENT_IDS_KEY}:${user?.id || 'guest'}`;

const readHiddenPatientIds = (user) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(getHiddenPatientIdsKey(user)) || '[]');
    return new Set(Array.isArray(parsed) ? parsed.map((id) => String(id)) : []);
  } catch (_) {
    return new Set();
  }
};

const saveHiddenPatientId = (user, id) => {
  const hiddenIds = readHiddenPatientIds(user);
  hiddenIds.add(String(id));
  localStorage.setItem(getHiddenPatientIdsKey(user), JSON.stringify([...hiddenIds]));
};

const getPatientImageVersionsKey = (user) =>
  `${PATIENT_IMAGE_VERSIONS_KEY}:${user?.id || 'guest'}`;

const readPatientImageVersions = (user) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(getPatientImageVersionsKey(user)) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

const savePatientImageVersion = (user, id, imageVersion) => {
  if (!id || !imageVersion) {
    return;
  }

  const versions = readPatientImageVersions(user);
  versions[String(id)] = String(imageVersion);
  localStorage.setItem(getPatientImageVersionsKey(user), JSON.stringify(versions));
};

const normalizePatient = (patient, user, imageVersions = {}) => {
  const item = readMap(patient);
  const profileName = readString(item.name);
  const firstName = readString(item.first_name || item.firstname);
  const lastName = readString(item.last_name || item.lastname);
  const derivedName =
    profileName || `${firstName} ${lastName}`.replace(/\s+/g, ' ').trim();
  const numericId =
    Number(item.id || item.patient_id || item.partner_id || 0) || 0;
  const isSelf =
    numericId > 0 &&
    [user?.patient_id, user?.default_patient_id]
      .map((value) => Number(value || 0))
      .includes(numericId);
  const alcoholConsumption = readString(item.alcohol_consumption) || ALCOHOL_NONE;
  const imageVersion =
    readString(imageVersions[String(numericId)]) ||
    resolvePatientImageVersion(item);

  return {
    id: String(numericId),
    numericId,
    name: derivedName,
    first_name: firstName || splitName(derivedName).firstName,
    last_name: lastName || splitName(derivedName).lastName,
    phone:
      readString(
        item.phone || item.phone_number || item.mobile || item.mobile_number
      ) || '',
    email: readString(item.email) || '',
    gender: readString(item.gender) || '',
    bloodGroup: readString(item.blood_type || item.blood_group) || '',
    dateOfBirth: readString(item.date_of_birth) || '',
    weight: readNumberString(item.weight),
    height: readNumberString(item.height),
    smoker: readBool(item.smoker || item.is_smoker),
    smokingYears: readNumberString(item.smoking_years),
    alcoholConsumption,
    alcohol: alcoholConsumption !== ALCOHOL_NONE,
    exerciseFrequency: readString(item.exercise_frequency) || EXERCISE_NONE,
    familyHistory: readString(item.family_history),
    chronicDiseases: extractNames(item.chronic_diseases),
    permanentMedications: extractNames(item.permanent_medications),
    allergies: extractNames(item.allergies),
    surgeries: extractNames(item.surgeries),
    vaccines: extractNames(item.vaccines),
    imageUrl: resolvePatientImageUrl(item),
    imageVersion,
    appointmentCount: Number(item.appointment_count || item.appointments_count || 0) || 0,
    relationship: isSelf ? 'self' : readString(item.relationship) || 'other',
    isDefault: readBool(item.is_default, isSelf),
    isSelf,
    userId: item.user_id ?? null,
  };
};

const buildPatientPayload = (
  member,
  { includeDefault = true, phoneVerificationToken = null } = {}
) => {
  const { firstName, lastName } = splitName(member.name);
  const imageFile = isFileLike(member.imageFile || member.image_file)
    ? member.imageFile || member.image_file
    : null;

  return {
    name: readString(member.name),
    first_name: firstName,
    last_name: lastName,
    phone_number: readString(member.phone),
    phone: readString(member.phone),
    email: readString(member.email),
    gender: readString(member.gender),
    blood_type: readString(member.bloodGroup),
    date_of_birth: readString(member.dateOfBirth),
    ...(readString(phoneVerificationToken)
      ? { phone_verification_token: readString(phoneVerificationToken) }
      : {}),
    ...(includeDefault ? { is_default: !!member.isDefault } : {}),
    ...(imageFile ? { image: imageFile } : {}),
  };
};

const buildMedicalPayload = (member) => ({
  name: readString(member.name),
  phone: readString(member.phone),
  email: readString(member.email),
  gender: readString(member.gender),
  date_of_birth: readString(member.dateOfBirth),
  blood_type: readString(member.bloodGroup),
  height: Number(member.height || 0),
  weight: Number(member.weight || 0),
  chronic_diseases: toListPayload(member.chronicDiseases),
  permanent_medications: toListPayload(member.permanentMedications),
  allergies: toListPayload(member.allergies),
  surgeries: toListPayload(member.surgeries),
  vaccines: toListPayload(member.vaccines),
  is_smoker: !!member.smoker,
  smoking_years: Number(member.smokingYears || 0),
  alcohol_consumption: readString(member.alcoholConsumption) || ALCOHOL_NONE,
  exercise_frequency: readString(member.exerciseFrequency) || EXERCISE_NONE,
  family_history: readString(member.familyHistory),
});

const extractErrorMessage = (error) =>
  readString(
    error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message
  );

const shouldFallbackToAccountProfile = (error) => {
  if (error?.response?.status === 404) {
    return true;
  }

  const message = extractErrorMessage(error).toLowerCase();
  return (
    message.includes('not found') ||
    message.includes('patient not found') ||
    message.includes('no patient')
  );
};

const assertSuccessfulResponse = (response, fallbackMessage) => {
  const payload = readMap(response?.data);
  if (payload.success === false) {
    throw new Error(readString(payload.message || payload.error) || fallbackMessage);
  }
};

const findAssignedDefaultMember = (members, excludingId) =>
  members.find((member) => {
    if (!member.isDefault) {
      return false;
    }

    return String(member.id) !== String(excludingId || '');
  }) || null;

const createDefaultPatientConflictError = (defaultMember) => {
  const name = readString(defaultMember?.name) || 'patient';
  const error = new Error(
    `Another default patient is already assigned: ${name}. Please remove the current default first.`
  );
  error.code = 'DEFAULT_PATIENT_CONFLICT';
  error.defaultPatient = defaultMember;
  return error;
};

const assertCanAssignDefaultMember = (members, member) => {
  if (!member?.isDefault) {
    return;
  }

  const currentDefault = findAssignedDefaultMember(members, member.id);
  if (currentDefault) {
    throw createDefaultPatientConflictError(currentDefault);
  }
};

const hasImageFile = (member) =>
  isFileLike(member?.imageFile || member?.image_file || member?.image);

const applyImageVersionToMember = (members, id, imageVersion) =>
  members.map((member) =>
    String(member.id) === String(id)
      ? { ...member, imageVersion: imageVersion || member.imageVersion }
      : member
  );

export const FamilyProvider = ({ children }) => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, user, updateUser } = useAuth();

  const persistMembers = useCallback((members) => {
    if (members.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(members));
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const hydrateFromStorage = useCallback(() => {
    const savedMembers = localStorage.getItem(STORAGE_KEY);
    if (!savedMembers) {
      setFamilyMembers([]);
      setSelectedMember(null);
      return [];
    }

    try {
      const parsed = JSON.parse(savedMembers);
      const members = Array.isArray(parsed) ? parsed : [];
      setFamilyMembers(members);
      setSelectedMember(members.find((member) => member.isDefault) || members[0] || null);
      return members;
    } catch (error) {
      console.error('Failed to parse family members:', error);
      setFamilyMembers([]);
      setSelectedMember(null);
      return [];
    }
  }, []);

  const applyMembers = useCallback(
    (members) => {
      const orderedMembers = [...members].sort((a, b) => {
        if (a.isDefault !== b.isDefault) {
          return a.isDefault ? -1 : 1;
        }

        return a.name.localeCompare(b.name);
      });

      setFamilyMembers(orderedMembers);
      setSelectedMember(
        orderedMembers.find((member) => member.isDefault) || orderedMembers[0] || null
      );
      persistMembers(orderedMembers);
      return orderedMembers;
    },
    [persistMembers]
  );

  const fetchFamilyMembers = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      return hydrateFromStorage();
    }

    setLoading(true);
    try {
      const response = await patientService.getByUserId();
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      const hiddenIds = readHiddenPatientIds(user);
      const imageVersions = readPatientImageVersions(user);
      const normalizedMembers = rows
        .map((patient) => normalizePatient(patient, user, imageVersions))
        .filter((member) => member.numericId > 0 && !hiddenIds.has(member.id));

      return applyMembers(normalizedMembers);
    } catch (error) {
      console.error('Failed to fetch family members:', error);
      return hydrateFromStorage();
    } finally {
      setLoading(false);
    }
  }, [applyMembers, hydrateFromStorage, isAuthenticated, user]);

  useEffect(() => {
    fetchFamilyMembers();
  }, [fetchFamilyMembers]);

  const updateAccountProfile = useCallback(async (payload) => {
    let lastError = null;

    for (const updateProfile of [
      () => patientService.updateProfile(payload),
      () => authService.updateCurrentUser(payload),
    ]) {
      try {
        const response = await updateProfile();
        assertSuccessfulResponse(response, 'Failed to update patient profile');
        return response;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Failed to update patient profile');
  }, []);

  const syncAccountMemberProfile = useCallback(
    (member, imageVersion = '') => {
      if (!member || !(member.isDefault || member.isSelf)) {
        return;
      }

      const updates = {
        profile_image_updated_at: imageVersion || member.imageVersion || Date.now(),
      };

      if (member.name) {
        updates.name = member.name;
      }
      if (member.first_name) {
        updates.first_name = member.first_name;
      }
      if (member.last_name) {
        updates.last_name = member.last_name;
      }
      if (member.email) {
        updates.email = member.email;
      }
      if (member.phone) {
        updates.phone = member.phone;
        updates.phone_number = member.phone;
      }
      if (member.imageUrl) {
        updates.image = member.imageUrl;
        updates.avatar = member.imageUrl;
        updates.patient = {
          id: member.numericId || Number(member.id) || member.id,
          image: member.imageUrl,
          avatar: member.imageUrl,
        };
      }

      updateUser(updates);
    },
    [updateUser]
  );

  const saveMedicalRecord = useCallback(async (member, patientId, options = {}) => {
    const payload = buildMedicalPayload(member);
    const normalizedPatientId = Number(patientId || member?.numericId || member?.id || 0) || 0;
    const shouldUseAccountProfile = !!(options && options.useAccountProfile);

    try {
      if (!shouldUseAccountProfile && normalizedPatientId > 0) {
        await medicalRecordService.updatePatientMedicalRecord(normalizedPatientId, payload);
        return;
      }

      await medicalRecordService.createMedicalHistory(payload);
    } catch (error) {
      if (
        !shouldUseAccountProfile &&
        (member?.isDefault || member?.isSelf) &&
        shouldFallbackToAccountProfile(error)
      ) {
        try {
          await medicalRecordService.createMedicalHistory(payload);
          return;
        } catch (fallbackError) {
          console.error('Failed to save account medical data:', fallbackError);
          return;
        }
      }

      console.error('Failed to save patient medical data:', error);
    }
  }, []);

  const addFamilyMember = useCallback(
    async (member) => {
      assertCanAssignDefaultMember(familyMembers, member);

      const payload = buildPatientPayload(
        {
          ...member,
          isDefault: familyMembers.length === 0 || !!member.isDefault,
        },
        {
          includeDefault: true,
          phoneVerificationToken: member.phoneVerificationToken || null,
        }
      );

      const response = await patientApi.createPatient(payload);
      const saved = readMap(response?.data?.data).id ? response.data.data : response?.data;
      const savedId =
        Number(saved?.id || saved?.patient_id || saved?.partner_id || 0) || 0;

      if (savedId > 0) {
        await saveMedicalRecord(member, savedId);
      }

      const uploadedImageVersion = hasImageFile(member) ? Date.now() : '';
      savePatientImageVersion(user, savedId, uploadedImageVersion);
      const refreshedMembers = await fetchFamilyMembers();
      const nextMembers = uploadedImageVersion
        ? applyMembers(applyImageVersionToMember(refreshedMembers, savedId, uploadedImageVersion))
        : refreshedMembers;
      const savedMember = nextMembers.find((item) => String(item.id) === String(savedId));
      syncAccountMemberProfile(savedMember, uploadedImageVersion);
      return saved;
    },
    [
      applyMembers,
      familyMembers,
      fetchFamilyMembers,
      saveMedicalRecord,
      syncAccountMemberProfile,
      user,
    ]
  );

  const updateFamilyMember = useCallback(
    async (id, updates, options = {}) => {
      const existingMember = familyMembers.find((member) => member.id === String(id));
      if (!existingMember) {
        throw new Error('Patient not found');
      }

      const mergedMember = { ...existingMember, ...updates };
      assertCanAssignDefaultMember(familyMembers, mergedMember);

      const payload = buildPatientPayload(mergedMember, {
        includeDefault: true,
        phoneVerificationToken: options.phoneVerificationToken || null,
      });

      const patientId = existingMember.numericId || Number(id) || id;
      let savedViaAccountProfile = false;

      try {
        await patientApi.updatePatient(patientId, payload);
      } catch (error) {
        if (!shouldFallbackToAccountProfile(error)) {
          throw error;
        }

        savedViaAccountProfile = true;
        await updateAccountProfile(payload);
      }

      await saveMedicalRecord(mergedMember, existingMember.numericId, {
        useAccountProfile: savedViaAccountProfile,
      });
      const uploadedImageVersion = hasImageFile(mergedMember) ? Date.now() : '';
      savePatientImageVersion(user, id, uploadedImageVersion);
      const refreshedMembers = await fetchFamilyMembers();
      const nextMembers = uploadedImageVersion
        ? applyMembers(applyImageVersionToMember(refreshedMembers, id, uploadedImageVersion))
        : refreshedMembers;
      const refreshedMember =
        nextMembers.find((member) => String(member.id) === String(id)) ||
        mergedMember;
      syncAccountMemberProfile(refreshedMember, uploadedImageVersion);
      return mergedMember;
    },
    [
      applyMembers,
      familyMembers,
      fetchFamilyMembers,
      saveMedicalRecord,
      syncAccountMemberProfile,
      updateAccountProfile,
      user,
    ]
  );

  const removeFamilyMember = useCallback(
    async (id) => {
      const existingMember = familyMembers.find((member) => member.id === String(id));
      if (!existingMember) {
        throw new Error('Patient not found');
      }
      const shouldHidePrimaryOnly =
        existingMember.isSelf &&
        familyMembers.some((member) => member.id !== String(id));

      const response = await patientApi.deletePatient(id);
      const payload = readMap(response?.data);
      const responseData = readMap(payload.data);
      const accountDeleted = readBool(
        responseData.account_deleted ?? payload.account_deleted
      );
      const message = readString(responseData.message) || readString(payload.message);

      if (shouldHidePrimaryOnly) {
        saveHiddenPatientId(user, id);
        const refreshedMembers = await fetchFamilyMembers();
        const visibleMembers = (
          Array.isArray(refreshedMembers) ? refreshedMembers : familyMembers
        ).filter((member) => member.id !== String(id));
        applyMembers(visibleMembers);
        return { accountDeleted: false, hiddenPrimary: true, message };
      }

      if (accountDeleted) {
        localStorage.removeItem(STORAGE_KEY);
        setFamilyMembers([]);
        setSelectedMember(null);
        return { accountDeleted, message };
      }

      await fetchFamilyMembers();
      return { accountDeleted: false, message };
    },
    [applyMembers, familyMembers, fetchFamilyMembers, user]
  );

  const setDefaultMember = useCallback(
    async (id) => {
      const existingMember = familyMembers.find((member) => member.id === String(id));
      if (!existingMember) {
        throw new Error('Patient not found');
      }

      await updateFamilyMember(id, { ...existingMember, isDefault: true });
    },
    [familyMembers, updateFamilyMember]
  );

  const selectMemberForBooking = useCallback((member) => {
    setSelectedMember(member);
  }, []);

  return (
    <FamilyContext.Provider
      value={{
        familyMembers,
        selectedMember,
        loading,
        fetchFamilyMembers,
        addFamilyMember,
        updateFamilyMember,
        removeFamilyMember,
        setDefaultMember,
        selectMemberForBooking,
      }}
    >
      {children}
    </FamilyContext.Provider>
  );
};

export const useFamily = () => {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamily must be used within FamilyProvider');
  }

  return context;
};
